import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import 'rxjs/add/operator/toPromise';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import { CookieService } from 'ngx-cookie-service';
import { Log, Level } from 'ng2-logger/client';
import { Observable, Observer } from 'rxjs/Rx';
import { likelihood } from '../../views/contacts/contacts-lookups';
import { environment } from '../../../environments/environment';
import { Subscription } from 'rxjs';

const log = Log.create('api-endpoints.service.ts');

@Injectable()
/**
 * @author Malek Motan, 2018
 * */
export class ApiEndpoints {
  private getRequests = {};
  private token = this.cookie.get('squanchy');
  private headerOptions;
  environmentApiUrl = environment.apiDomain;
  private getSub: Subscription;
  private postSub: Subscription;
  private nulledPostSub: Subscription;

  constructor(
    private httpService: HttpClient,
    private router: Router,
    private cookie: CookieService,
  ) {}

  /**
   * @description Get the endopints of the services to be called
   */
  endpoints() {
    return {
      login: () => '/api/login',
      googleOAuth: () => '/api/login-with-google',
      refreshToken: () => '/api/refresh-token',
      getLeadProfile: pathParams => `/api/leads/${pathParams.id}/profile`,
      editLeadProfile: id => '/api/agent/lead/crm/edit',
      getLeadSessionData: pathParams =>
        `/api/leads/${pathParams.leadId}/sessions/aggregation`,
      leadSearchHistory: pathParams =>
        `/api/leads/${pathParams.leadId}/history/${pathParams.source}`,
      leadProperties: pathParams =>
        `/api/leads/${pathParams.leadId}/history/properties`,
      leadSavedHomes: pathParams =>
        `/api/agents/lead/${pathParams.leadId}/saved_homes`,
      getLeadEvents: pathParams =>
        `/api/activity-timeline/agent/${pathParams.agentId}/lead/${pathParams.leadId}`,
      editLeadEvents: pathParams =>
        `/api/activity-timeline/${pathParams.activityId}/edit`,
      deleteLeadEvents: pathParams =>
        `/api/activity-timeline/${pathParams.activityId}/delete`,
      postLeadEvents: () => '/api/activity-timeline/add',
      getProfileMeta: () => `/api/agent/profile`,
      agentPropertyUpload: () => `/api/agent/property/upload`,
      getAgentSettings: () => `/api/agent/setting`,
      upsertAgentSettings: () => `/api/agent/setting`,
      sendEmail: () => '/api/agent/mail/send',
      leadsSearch: () => '/api/agents/leads/search',
      leads: (pathParams: {
        page;
        pageCount;
        term;
        order;
        sort_by;
        likelihood;
        city;
        state;
        gender;
        property_type;
        financing_type;
        marital_status;
        budget;
      }) =>
        `/api/leads?page=${pathParams.page}&page_count=${pathParams.pageCount}&term=${pathParams.term}&order=${pathParams.order}&sort_by=${pathParams.sort_by}&likelihood=${pathParams.likelihood}&city=${pathParams.city}&state=${pathParams.state}&gender=${pathParams.gender}&property_type=${pathParams.property_type}&financing_type=${pathParams.financing_type}&marital_status=${pathParams.marital_status}&budget=${pathParams.budget}`,
      updateLeadInfo: () => '/api/agent/lead/data',
      getGrantedLicenses: () => '/api/agent/grant-license',
      grantNewLicense: () => '/api/agent/grant-license',
      revokeLicense: pathParams =>
        `/api/agent/revoke-license/${pathParams.userId}`,
      mailSettings: () => '/api/agent/setting',
      webHookUrl: () => '/api/agent/web-hook/url',
      agencyAgent: () => '/api/agency/agents',
      getProfileSubscription: () => '/api/agent/subscriptions',
      getAgentAgency: () => '/api/agent/agency',
      uploadLeadImage: () => `/api/file/upload/base64Image`,
      deleteLead: pathParams => `/api/lead/${pathParams.leadId}/delete`,
      CRMcreate: () => `/api/agent/lead/crm/create`,
      GeoLocations: () => `/api/geo-locations/`,
      uploadCSV: () => `/api/agent/lead/list/upload`,
      saveCsvSettings: () => `/api/agent/lead/list/save`,
      agentEvents: () => `/api/agent/events`,
      addAgentEvents: () => `/api/agent/events`,
      deleteAgentEvent: () => `/api/agent/events/delete`,
      editProperty: () => `/api/agent/property/edit`,
      groupsOfContacts: () => `/api/agents/agent-email-groups`,
      deleteGroupOfContacts: pathParams =>
        `/api/agents/agent-email-groups/${pathParams.groupId}/delete`,
      addContactsToGroup: () => `/api/agents/agent-email-groups/add-leads`,
      getGroupContacts: pathParams =>
        `/api/agents/agent-email-groups/${pathParams.groupId}/leads`,
      deleteContactFromGroup: pathParams =>
        `/api/agents/agent-email-groups/${pathParams.group_id}/leads/${pathParams.lead_id}/delete`,
      makeFavoriteGroup: pathParams =>
        `/api/agents/agent-email-groups/${pathParams.groupId}/${pathParams.is_favorite}`,
      getIdxProperties: () => `/api/agents/mls-properties`,
      getpropertyFinderList: () => '/api/property/finder/list',
      redfinEstimate: pathParams =>
        `/api/property/redfinEstimation/${pathParams.propertyId}`,
      startBatchPdfFiles: () => `/api/agent/start-batch-pdf-files`,
      checkBatchPdfFiles: () => `/api/agent/check-batch-pdf-files`,
      agentwelcomePopupForm: () => `/api/agent/welcome-popup`,
      getOwnerList: () => `/api/agent/home-owners`,
      exportOwnerList: () => `/api/agent/home-owners/export`,
      getOffMarketPropertyList: () => `/api/property/market-place/list`,
      fetchPropertyRecommender: pathParams =>
        `/api/agents/lead/${pathParams.leadId}/recommender`,
      fetchLicenceCount: () => `/api/agent/num-granted-license`,
      getProfilePermissions: () => '/api/Agent/Permissions',
    };
  }

  /**
   * @description Get the default request headers
   */
  getHeaders() {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.token}`,
    });
  }

  getCSVHeaders() {
    return new HttpHeaders({
      Authorization: `Bearer ${this.token}`,
    });
  }

  /**
   * @description This method is used to get HTTP requests to the desired endpoint.
   *
   * @param  {string} route
   * @param  {object} requestBody
   * @param  {object} pathParams
   *
   * @returns {object} http response
   */
  get(route, requestBody, pathParams?) {
    return Observable.create((observer: Observer<any>) => {
      const url = this.endpoints()[route](pathParams);
      const params: URLSearchParams = new URLSearchParams();
      // tslint:disable-next-line:forin
      for (let key in requestBody) {
        params.set(key, requestBody[key]);
      }

      const headers = this.getHeaders();
      const requestOptions = {
        headers: headers,
        params: requestBody,
      };
      this.getSub = this.httpService.get(url, requestOptions).subscribe(
        (data: any) => {
          log.i('GET:Success---', data);
          observer.next(data.content);
        },
        error => {
          log.er('GET:Error---', error);
          observer.error(error);
        },
      );
      return this.getSub;
    });
  }

  /**
   * @description This method is used to send HTTP requests to the desired endpoint.
   *
   * @param  {string} route
   * @param  {object} requestData
   *
   * @param pathParams
   * @returns {object} http response
   */
  post(route, requestData, pathParams?, queryParams?): Observable<any> {
    return Observable.create((observer: Observer<any>) => {
      const url = this.endpoints()[route](pathParams);
      const postData = requestData;
      if (url === '/api/agent/lead/list/upload') {
        this.headerOptions = this.getCSVHeaders();
      } else {
        this.headerOptions = this.getHeaders();
      }

      const params: URLSearchParams = new URLSearchParams();
      for (let key in queryParams) {
        params.set(key, queryParams[key]);
      }
      const requestOptions = {
        headers: this.headerOptions,
        params: queryParams ? queryParams : null,
      };

      this.postSub = this.httpService
        .post(url, postData, requestOptions)
        .subscribe(
          data => {
            log.i('POST:Success---', data);
            observer.next(data);
          },
          err => {
            log.i('POST:Error---', err);
            observer.error(err);
            if (err && err.code && err.code === 401) {
              this.cookie.delete('squanchy');
              this.router.navigate(['/login']);
            }
          },
        );
      return this.postSub;
    });
  }

  fetchBaseDomainName() {
    /**
     * Dynamic fetch of loading page domain
     *
     * Cases:
     *
     * 1) virtual domain (localhost)
     * 2) real domain (*.X.M)
     *
     * The domain use for cookies to allow subdomains is (.X.M)
     */
    var hostName = location.hostname;
    if (hostName) {
      var hostNameList = hostName.split('.');
      if (hostNameList.length < 2) {
        var baseDomain = hostNameList[hostNameList.length - 1];
      } else {
        var secondLevelDomain = hostNameList[hostNameList.length - 2];
        var topLevelDomain = hostNameList[hostNameList.length - 1];
        var baseDomain = `.${secondLevelDomain}.${topLevelDomain}`;
      }
      return baseDomain || null;
    }
  }

  nulledPost(route, data): Observable<any> {
    return Observable.create((observer: Observer<any>) => {
      let url = this.endpoints()[route]();
      let data_ready = data;
      let headers = new Headers({ 'Content-Type': 'application/json' });
      let options = {
        headers: headers,
      };

      this.nulledPostSub = this.post(url, data_ready, options)
        .map((res: Response) => {
          var data = res.json();
          return data;
        })
        .subscribe(
          data => {
            observer.next({
              status: true,
              data: data,
            });
            this.nulledPostSub.unsubscribe();
          },
          err => {
            var data = err.json();
            observer.error({
              status: false,
              error: data,
            });
            this.nulledPostSub.unsubscribe();
          },
        );
    });
  }
}
